import { useState, useRef } from 'react';
import { Activity, MessageSquare } from 'lucide-react';

interface LoginPageProps {
    onLogin: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
    const [step, setStep] = useState<'phone' | 'otp'>('phone');
    const [phone, setPhone] = useState('+57');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [sending, setSending] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    async function handleSendOtp() {
        if (phone.length < 10) return;
        setSending(true);
        // Simulate OTP send via WhatsApp
        await new Promise(r => setTimeout(r, 1000));
        setSending(false);
        setStep('otp');
    }

    async function handleVerifyOtp() {
        const code = otp.join('');
        if (code.length < 6) return;
        setSending(true);
        // Simulate OTP verification
        await new Promise(r => setTimeout(r, 800));
        setSending(false);
        onLogin();
    }

    function handleOtpChange(index: number, value: string) {
        if (!/^\d*$/.test(value)) return;
        const next = [...otp];
        next[index] = value.slice(-1);
        setOtp(next);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    }

    function handleOtpKeyDown(index: number, e: React.KeyboardEvent) {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }

    return (
        <div className="login-screen fade-in">
            <div className="login-logo">
                <Activity size={32} />
            </div>
            <h1 className="login-title">Mi Salud</h1>
            <p className="login-subtitle">Portal del Paciente — Vriksha Health</p>

            {step === 'phone' ? (
                <div style={{ width: '100%', maxWidth: 360 }}>
                    <div style={{ marginBottom: 20 }}>
                        <label className="portal-label">Número de WhatsApp</label>
                        <input
                            className="portal-input"
                            type="tel"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            placeholder="+573001234567"
                            id="phone-input"
                        />
                    </div>
                    <button className="portal-btn portal-btn-whatsapp" onClick={handleSendOtp} disabled={sending} id="send-otp-btn">
                        <MessageSquare size={20} />
                        {sending ? 'Enviando...' : 'Enviar código por WhatsApp'}
                    </button>
                    <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-text-muted)', marginTop: 16 }}>
                        Le enviaremos un código de verificación de 6 dígitos a su WhatsApp
                    </p>
                </div>
            ) : (
                <div style={{ width: '100%', maxWidth: 360 }}>
                    <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20 }}>
                        Código enviado a <strong>{phone}</strong>
                    </p>
                    <div className="otp-input-group">
                        {otp.map((digit, i) => (
                            <input
                                key={i}
                                ref={el => { inputRefs.current[i] = el; }}
                                className="otp-digit"
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={e => handleOtpChange(i, e.target.value)}
                                onKeyDown={e => handleOtpKeyDown(i, e)}
                                id={`otp-${i}`}
                            />
                        ))}
                    </div>
                    <button className="portal-btn portal-btn-primary" onClick={handleVerifyOtp} disabled={sending} id="verify-otp-btn">
                        {sending ? 'Verificando...' : 'Verificar código'}
                    </button>
                    <button
                        className="portal-btn portal-btn-secondary"
                        onClick={() => { setStep('phone'); setOtp(['', '', '', '', '', '']); }}
                        style={{ marginTop: 12, fontSize: 14 }}
                    >
                        Cambiar número
                    </button>
                </div>
            )}
        </div>
    );
}
